import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { canonicalSearchURL } from './fivepoint-search-query.js';
import { FacetGroups, applyNativeGroups } from './fivepoint-atelier-facet-groups.js?v=096d41c0b84d2815';

/** Native Shopify filters/40-item pages; local grouping only translates facet controls. */
class AtelierCatalog extends Component {
  #listeners;
  #request;
  #timer;
  #pendingURL;
  #pendingHistory = true;
  #wide;
  #groups;
  #dictionary;
  #countCache = new Map();

  connectedCallback() {
    super.connectedCallback();
    this.#listeners?.abort();
    this.#listeners = new AbortController();
    const options = { signal: this.#listeners.signal };
    this.addEventListener('submit', this.#submit, options);
    this.addEventListener('change', this.#change, options);
    this.addEventListener('click', this.#click, options);
    this.addEventListener('input', event => {
      if (event.target.matches('input[name="q"]') && !event.target.value.trim()) { this.#apply(event.target.form); return; }
      const facet = event.target.closest('[data-fp-facet]');
      if (event.target.matches('[data-fp-facet-search]') && facet) this.#facet(facet);
    }, options);
    this.addEventListener('keydown', this.#keyboard, options);
    window.addEventListener('popstate', () => this.#navigate(new URL(location.href), false), options);
    this.#wide = matchMedia('(min-width: 901px)');
    this.#wide.addEventListener('change', this.#resize, options);
    this.#enhance();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    this.#request?.abort();
    this.#groups?.dispose();
    clearTimeout(this.#timer);
    document.documentElement.classList.remove('fp-filter-lock');
  }

  #enhance() {
    this.dataset.enhanced = 'true';
    this.#groups?.dispose();
    this.#groups = new FacetGroups(this,this.#countCache);
    this.#groups.enhance(new URL(location.href));
    this.querySelectorAll('[data-fp-facet]').forEach(facet => this.#facet(facet));
    this.querySelectorAll('[data-atelier-taxonomy-group]').forEach(group => {
      const toggle = group.querySelector('[data-atelier-taxonomy-toggle]');
      if (!toggle) return;
      const inputs = [...group.querySelectorAll('[data-taxonomy-value]')];
      const selected = inputs.filter(input => input.checked).length;
      toggle.checked = selected > 0 && selected === inputs.length;
      toggle.indeterminate = selected > 0 && selected < inputs.length;
      toggle.parentElement.removeAttribute('inert');
    });
  }

  #facet(facet) {
    const search = facet.querySelector('[data-fp-facet-search]');
    if (!search) return;
    const normalise = value => value.normalize('NFKD').replace(/\p{M}/gu, '').toLowerCase().trim();
    const query = normalise(search.value);
    const expanded = facet.dataset.expanded === 'true';
    let count = 0;
    facet.querySelectorAll('.fp-facet-option').forEach((option, index) => {
      const matches = query ? normalise(option.querySelector('.fp-facet-option__text').textContent).includes(query)
        : expanded || index < 10 || option.querySelector('input').checked;
      option.hidden = !matches;
      if (matches) count++;
    });
    search.closest('.fp-facet-search').hidden = false;
    const more = facet.querySelector('[data-fp-facet-more]');
    more.hidden = !!query;
    more.setAttribute('aria-expanded', String(expanded));
    more.querySelector('[data-fp-facet-more-icon]').textContent = expanded ? '−' : '+';
    more.querySelector('[data-fp-facet-more-label]').textContent = expanded ? more.dataset.less : more.dataset.more;
    facet.querySelector('.fp-facet-empty').hidden = count > 0;
  }

  #submit = event => {
    if (!event.target.matches('[data-atelier-filter-form]')) return;
    event.preventDefault();
    this.#apply(event.target);
  };

  #change = event => {
    const input = event.target;
    if (!input.form?.matches('[data-atelier-filter-form]')) return;
    if (this.#groups.change(input)) { this.#apply(input.form, input.name); return; }
    if (input.matches('[data-atelier-taxonomy-toggle]')) {
      const values = [...input.closest('[data-atelier-taxonomy-group]').querySelectorAll('[data-taxonomy-value]')];
      // A category is the union of its native values, including currently empty
      // siblings. Keep the whole category when another axis subsequently changes.
      values.forEach(value => {
        value.checked = input.checked;
        value.disabled = !input.checked && value.hasAttribute('data-taxonomy-zero');
      });
      this.#apply(input.form, values[0]?.name);
      return;
    }
    if (!input.matches('input[type="checkbox"],select[name="sort_by"]')) return;
    this.#apply(input.form, input.name);
  };

  #apply(form, field) {
    const url = new URL(form.action, location.href);
    url.search = new URLSearchParams(new FormData(form)).toString();
    this.#groups.apply(url);
    url.searchParams.delete('page');
    const keys = {'filter.p.m.custom.sample_dispatch_24h':'availability','filter.p.product_type':'category',sort_by:'sort'};
    if (keys[field]) window.FivePointEvents?.emit('fp_filter_apply', {
      filter_key: keys[field], selected_count: field === 'sort_by' ? 1 : url.searchParams.getAll(field).length,
    }, {source_surface:'catalog',component_id:'catalog-filters'});
    this.#navigate(canonicalSearchURL(url));
  }

  #click = event => {
    const control = event.target.closest('a,button');
    if (!control || !this.contains(control)) return;
    if (control.matches('[data-atelier-card-flip]')) {
      const peek = control.getAttribute('aria-pressed') !== 'true';
      control.setAttribute('aria-pressed', String(peek));
      control.setAttribute('aria-label', peek ? control.dataset.coverLabel : control.dataset.detailLabel);
      control.closest('.fp-product-wrap')?.classList.toggle('is-peek', peek);
      return;
    }
    if (control.matches('[data-atelier-filter-open]')) this.#open(true);
    if (control.matches('[data-atelier-filter-close]')) this.#open(false);
    if (control.matches('[data-atelier-catalog-retry]') && this.#pendingURL) this.#navigate(this.#pendingURL, this.#pendingHistory);
    if (control.matches('[data-fp-facet-more]')) {
      const facet = control.closest('[data-fp-facet]');
      facet.dataset.expanded = String(facet.dataset.expanded !== 'true');
      this.#facet(facet);
    }
    if (!control.matches('.fp-pagination a,.fp-active-filters a,[data-atelier-clear]')) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || control.target === '_blank') return;
    const url = new URL(control.href);
    if (url.origin !== location.origin || url.pathname !== location.pathname) return;
    event.preventDefault();
    this.#navigate(url, true, control.closest('.fp-pagination') !== null);
  };

  #open(open, focus = true) {
    this.classList.toggle('fp-filters-open', open);
    document.documentElement.classList.toggle('fp-filter-lock', open);
    this.querySelector('[data-atelier-filter-open]')?.setAttribute('aria-expanded', String(open));
    const panel = this.querySelector('[data-atelier-filter-panel]');
    if (open) { panel.setAttribute('role','dialog'); panel.setAttribute('aria-modal','true'); }
    else { panel.removeAttribute('role'); panel.removeAttribute('aria-modal'); }
    if (focus) this.querySelector(open ? '[data-atelier-filter-panel] [data-atelier-filter-close]' : '[data-atelier-filter-open]')?.focus();
  }

  #resize = () => { if (this.#wide.matches) this.#open(false, false); };

  #keyboard = event => {
    if (event.target.matches('[data-fp-facet-search]')) {
      if (event.key === 'Enter') event.preventDefault();
      if (event.key === 'Escape' && event.target.value) {
        event.preventDefault(); event.target.value = ''; this.#facet(event.target.closest('[data-fp-facet]')); return;
      }
    }
    if (!this.classList.contains('fp-filters-open')) return;
    if (event.key === 'Escape') { event.preventDefault(); this.#open(false); }
    if (event.key !== 'Tab') return;
    const stops = [...this.querySelector('[data-atelier-filter-panel]').querySelectorAll('a,button,summary,input:not(:disabled),select')]
      .filter(el => !el.closest('[hidden]') && el.getClientRects().length);
    if (!stops.length) return;
    if (event.shiftKey && document.activeElement === stops[0]) { event.preventDefault(); stops.at(-1).focus(); }
    else if (!event.shiftKey && document.activeElement === stops.at(-1)) { event.preventDefault(); stops[0].focus(); }
  };

  async #navigate(target, push = true, pagination = false) {
    if (target.pathname !== location.pathname) { location.assign(target.href); return; }
    this.#request?.abort(); clearTimeout(this.#timer);
    this.#groups?.dispose();
    const controller = new AbortController(); this.#request = controller;
    const url = new URL(target); url.searchParams.delete('section_id'); url.hash = '';
    this.#pendingURL = url; this.#pendingHistory = push;
    const status = this.querySelector('[data-atelier-catalog-status]');
    const error = this.querySelector('[data-atelier-catalog-error]');
    status.setAttribute('role','status'); status.textContent = this.dataset.loading; error.hidden = true;
    this.setAttribute('aria-busy','true');
    this.querySelector('[data-atelier-results]').inert = true;
    let timedOut = false;
    this.#timer = setTimeout(() => { timedOut = true; controller.abort(); }, 15000);
    try {
      if (['fp_fibre','fp_weight','fp_width'].some(key => url.searchParams.has(key))) {
        if (!this.#dictionary) {
          const dictionaryURL = new URL(url); dictionaryURL.search = '';
          if (this.querySelector('[name="q"]')) { dictionaryURL.searchParams.set('q','*'); dictionaryURL.searchParams.set('type','product'); }
          dictionaryURL.searchParams.set('section_id','fivepoint-atelier-facet-dictionary');
          const response = await fetch(dictionaryURL,{signal:controller.signal,credentials:'same-origin',headers:{Accept:'text/html'}});
          if (!response.ok) throw new Error(`Facet dictionary HTTP ${response.status}`);
          const data = new DOMParser().parseFromString(await response.text(),'text/html').querySelector('[data-atelier-facet-dictionary]');
          if (!data) throw new Error('Missing facet dictionary');
          this.#dictionary = JSON.parse(data.textContent);
        }
        applyNativeGroups(url,this.#dictionary);
      }
      if (controller.signal.aborted) { if (timedOut) throw new Error('Facet dictionary timed out'); return; }
      const requestURL = new URL(url); requestURL.searchParams.set('section_id', this.dataset.sectionId);
      const response = await fetch(requestURL, {signal:controller.signal, credentials:'same-origin', headers:{Accept:'text/html'}});
      if (!response.ok) throw new Error(`Catalogue HTTP ${response.status}`);
      const html = await response.text();
      if (controller.signal.aborted) {
        if (timedOut) throw new Error('Catalogue timed out');
        return;
      }
      const parsed = new DOMParser().parseFromString(html,'text/html');
      const next = parsed.getElementById(`shopify-section-${this.dataset.sectionId}`)?.querySelector('fivepoint-atelier-catalog');
      if (next?.dataset.sectionId !== this.dataset.sectionId || !next.querySelector('[data-atelier-results]') || !next.querySelector('[data-atelier-filter-form]') || !next.querySelector('[data-hydration-key="atelier-catalog-content"]')) throw new Error('Invalid catalogue section');
      const active = document.activeElement;
      const focus = this.contains(active) ? {id:active.id, name:active.name, value:active.value} : null;
      const facets = [...this.querySelectorAll('[data-fp-facet]')].map(facet => ({key:facet.dataset.fpFacet, open:facet.open, expanded:facet.dataset.expanded, query:facet.querySelector('[data-fp-facet-search]')?.value}));
      await morphSection(this.dataset.sectionId, html, {mode:'hydration'});
      if (controller.signal.aborted || this.#request !== controller) return;
      if (push && url.href !== location.href) history.pushState({},'',url);
      for (const state of facets) {
        const facet = [...this.querySelectorAll('[data-fp-facet]')].find(el => el.dataset.fpFacet === state.key);
        if (!facet) continue;
        facet.open = state.open; facet.dataset.expanded = state.expanded || 'false';
        const search = facet.querySelector('[data-fp-facet-search]'); if (search) search.value = state.query || '';
      }
      this.#enhance(); this.#open(this.classList.contains('fp-filters-open'), false);
      if (focus) {
        const restore = [...this.querySelectorAll('input,select,button,a,summary')].find(el => focus.id ? el.id === focus.id : focus.name && el.name === focus.name && el.value === focus.value);
        restore?.focus({preventScroll:true});
      }
      const count = this.querySelector('[data-atelier-result-count]'); status.textContent = count?.textContent || '';
      if (pagination) { count?.focus({preventScroll:true}); count?.scrollIntoView({block:'start'}); }
      // Existing saved-fabric state and analytics use document-level delegation.
      document.dispatchEvent(new CustomEvent('atelier:catalog-updated'));
    } catch (failure) {
      if ((controller.signal.aborted && !timedOut) || this.#request !== controller) return;
      status.setAttribute('role','alert'); status.textContent = this.dataset.failed; error.hidden = false;
      this.querySelector('[data-atelier-catalog-reload]').href = url.href;
    } finally {
      if (this.#request === controller) {
        clearTimeout(this.#timer); this.setAttribute('aria-busy','false');
        // Failed filters must not leave apparently-current, clickable old results.
        this.querySelector('[data-atelier-results]').inert = !error.hidden;
      }
    }
  }
}
if (!customElements.get('fivepoint-atelier-catalog')) customElements.define('fivepoint-atelier-catalog', AtelierCatalog);
